import React, { useEffect, useRef, useState } from 'react';
import InputIconByKey from './InputIconByKey';
import RequiredAsteriskTooltip from './RequiredAsteriskTooltip';
import { IconKey } from '../../../types/system/icons';

interface OptionItem {
  value: string | number;
  label: string;
}

interface SelectFieldProps {
  label: string;
  name: string;
  value: string | number;
  onChange: (
    event:
      | React.ChangeEvent<{ name: string; value: string | number }>
      | {
          target: {
            name: string;
            value: string | number;
            options: { text: string }[];
            selectedIndex: number;
          };
        }
  ) => void;
  options: OptionItem[];
  placeholder?: string;
  error?: string;
  iconKey?: IconKey;
  disabled?: boolean;
  required?: boolean;
  rightElement?: React.ReactNode;
  withMargin?: boolean;
  /**
   * Sobrescribe la clave usada en el atributo `data-field` (para scroll
   * automático a errores) cuando difiere de `name`.
   */
  dataField?: string;
}

const SelectField: React.FC<SelectFieldProps> = ({
  label,
  name,
  value,
  onChange,
  options,
  placeholder = 'Seleccione una opción',
  error,
  iconKey,
  disabled,
  required,
  rightElement,
  withMargin = true,
  dataField,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const containerClass = `relative ${withMargin ? 'mb-5' : ''}`;

  const selectedOption = options.find(
    (opt) => String(opt.value) === String(value)
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className={containerClass} data-field={dataField ?? name}>
      <label className="mb-2.5 flex items-center gap-1 text-sm font-medium text-black dark:text-white">
        {label}
        {required && <RequiredAsteriskTooltip delay={400} />}
      </label>

      <div className={rightElement ? 'flex w-full items-stretch' : ''}>
        <div className="relative flex-1">
          <div
            className={`
              bg-gray-1 flex h-[50px] items-stretch overflow-hidden border
              border-stroke transition-all duration-200
              focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/10
              dark:border-strokedark dark:bg-meta-4
              ${
                rightElement
                  ? 'rounded-l-xl rounded-r-none border-r-0'
                  : 'rounded-xl'
              }
              ${disabled ? 'cursor-not-allowed opacity-70' : ''}
            `}
          >
            {iconKey && (
              <div className="flex items-center justify-center border-r border-stroke bg-gray-2 px-4 dark:border-strokedark dark:bg-boxdark-2">
                <InputIconByKey iconKey={iconKey} />
              </div>
            )}

            <div
              className={`
                flex flex-1 items-center truncate bg-transparent px-4 text-sm outline-none
                ${
                  disabled
                    ? 'cursor-not-allowed text-bodydark2'
                    : 'cursor-pointer text-black dark:text-white'
                }
              `}
              onClick={() => !disabled && setIsOpen((prev) => !prev)}
            >
              {selectedOption ? (
                selectedOption.label
              ) : (
                <span className="text-body dark:text-bodydark2">
                  {placeholder}
                </span>
              )}
            </div>

            <span
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation();
                if (!disabled) setIsOpen((prev) => !prev);
              }}
              className={`
                flex items-center pr-4 transition-transform duration-200
                ${
                  disabled
                    ? 'pointer-events-none opacity-50'
                    : 'cursor-pointer hover:opacity-80'
                }
              `}
            >
              <svg
                className={`
                  fill-current transition-transform duration-300 ease-in-out
                  ${
                    isOpen
                      ? 'rotate-180 text-primary'
                      : 'rotate-0 text-body dark:text-bodydark2'
                  }
                `}
                width="24"
                height="24"
                viewBox="0 0 24 24"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M5.29289 8.29289C5.68342 7.90237 6.31658 7.90237 6.70711 8.29289L12 13.5858L17.2929 8.29289C17.6834 7.90237 18.3166 7.90237 18.7071 8.29289C19.0976 8.68342 19.0976 9.31658 18.7071 9.70711L12.7071 15.7071C12.3166 16.0976 11.6834 16.0976 11.2929 15.7071L5.29289 9.70711C4.90237 9.31658 4.90237 8.68342 5.29289 8.29289Z"
                />
              </svg>
            </span>
          </div>

          {isOpen && (
            <ul className="absolute left-0 top-full z-999999 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
              <li
                key="empty"
                className={`
                  cursor-default border-b border-stroke px-4 py-2 text-sm text-body
                  dark:border-strokedark dark:text-bodydark2
                  ${value === '' ? 'bg-gray-2 dark:bg-boxdark-2' : ''}
                `}
              >
                {placeholder}
              </li>

              {options.map((option) => (
                <li
                  key={option.value}
                  onClick={() => {
                    const fakeEvent = {
                      target: {
                        name,
                        value: option.value,
                        options: options.map((opt) => ({ text: opt.label })),
                        selectedIndex: options.findIndex(
                          (opt) => String(opt.value) === String(option.value)
                        ),
                      },
                    } as unknown as React.ChangeEvent<HTMLSelectElement>;

                    onChange(fakeEvent);
                    setIsOpen(false);
                  }}
                  className={`
                    cursor-pointer border-b border-stroke px-4 py-2 text-sm text-black transition
                    hover:bg-primary hover:text-white
                    dark:border-strokedark dark:text-white
                    ${
                      String(option.value) === String(value)
                        ? 'bg-primary text-white dark:bg-primary'
                        : ''
                    }
                  `}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          )}
        </div>

        {rightElement && (
          <div className="flex h-[50px] shrink-0 items-stretch">
            {rightElement}
          </div>
        )}
      </div>

      {error && <p className="mt-2 text-sm text-danger">{error}</p>}
    </div>
  );
};

export default SelectField;
